import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, FormattedDate } from 'react-intl';

import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import messages from '../RepaymentsSchedule.messages';
import styles from '../RepaymentsSchedule.module.css';
import { PlusSVG } from '@svgComponents/plusSVG';
import { GetExampleRepaymentScheduleResponse } from '@iwoca/lapi-client/edge';

const FormatRepaymentDate = ({
    repayment,
    index,
    overrideDateFormat
}: {
    repayment: any;
    index: number;
    overrideDateFormat?: (date: string, index: number) => string;
}) => {
    if (overrideDateFormat)
        return <>{overrideDateFormat(repayment.date, index)}</>;

    return (
        <FormattedDate
            day="numeric"
            month="short"
            year="numeric"
            value={new Date(repayment.date)}
        />
    );
};

export const NewRepaymentsSchedule = ({
    schedule,
    totalInterest,
    totalPrinciple,
    totalPayable,
    overrideScheduleDates
}: {
    schedule: GetExampleRepaymentScheduleResponse['data'];
    totalInterest: number;
    totalPrinciple: number;
    totalPayable: number;
    overrideScheduleDates?: any;
}) => {
    const scrollableTable = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (scrollableTable.current) {
            scrollableTable.current.scrollTo({
                top: 0,
                left: 130,
                behavior: 'smooth'
            });
        }
        setSliceScheduleBy(3);
    }, [scrollableTable, schedule]);

    const [sliceScheduleBy, setSliceScheduleBy] = useState(3);

    return (
        <div ref={scrollableTable} className={styles.scrollableTableContainer}>
            <table className={styles.repaymentsTable} cellSpacing="0">
                <thead>
                    <tr>
                        <th
                            className={classNames(
                                styles.dateColumn,
                                styles.fixed,
                                styles.fixedHeader
                            )}
                        >
                            <FormattedMessage {...messages.dateTitle} />
                        </th>

                        <FormattedMessage
                            tagName="th"
                            {...messages.principleTitle}
                        />

                        <FormattedMessage
                            tagName="th"
                            {...messages.interestTitle}
                        />

                        <FormattedMessage
                            tagName="th"
                            {...messages.totalTitle}
                        />
                    </tr>
                </thead>

                <tbody data-testid="statements">
                    {schedule
                        .slice(0, sliceScheduleBy)
                        .map((repayment: any, index: number) => (
                            <tr
                                data-testid="repayment-schedule-row"
                                key={`repayment-schedule-row-${repayment.date}`}
                                className={styles.light}
                            >
                                <td
                                    className={classNames(
                                        styles.dateColumn,
                                        styles.fixed,
                                        styles.fixedBody
                                    )}
                                >
                                    <FormatRepaymentDate
                                        overrideDateFormat={
                                            overrideScheduleDates
                                        }
                                        repayment={repayment}
                                        index={index}
                                    />
                                </td>

                                <td>
                                    <FormattedCurrency
                                        value={repayment.principal}
                                    />
                                </td>

                                <td>
                                    <FormattedCurrency
                                        value={repayment.interest}
                                    />
                                </td>

                                <td className={styles.total}>
                                    <FormattedCurrency
                                        value={repayment.total}
                                    />
                                </td>
                            </tr>
                        ))}
                    {schedule.length > 3 && sliceScheduleBy === 3 && (
                        <tr className={styles.fullScheduleWrapper}>
                            <td colSpan={4} className={styles.fullSchedule}>
                                <button
                                    onClick={() =>
                                        setSliceScheduleBy(schedule.length)
                                    }
                                >
                                    View full schedule{' '}
                                    <PlusSVG
                                        fill="#205DAB"
                                        className={styles.plusSvg}
                                    />
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>

                <tfoot>
                    <tr>
                        <td
                            className={classNames(
                                styles.total,
                                styles.fixed,
                                styles.fixedFooter
                            )}
                        >
                            <FormattedMessage {...messages.totalTitle} />
                        </td>

                        <td
                            data-testid="order-amount-table"
                            className={styles.light}
                        >
                            <FormattedCurrency value={totalPrinciple} />
                        </td>

                        <td
                            data-testid="total-interest"
                            className={styles.light}
                        >
                            <FormattedCurrency value={totalInterest} />
                        </td>

                        <td
                            data-testid="total-payable"
                            className={styles.total}
                        >
                            <FormattedCurrency value={totalPayable} />
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};
